import React from 'react';

import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { messageOfFailure } from '@oysterjs/core/graphql/errors';

import {
  AmsAccountMatchReason,
  type ClientAmsAccountFieldsFragment,
  type SuggestedAmsAccountsQuery,
  useLinkClientAmsAccountMutation,
  useSuggestedAmsAccountsQuery,
  useUnlinkClientAmsAccountMutation
} from '~/api/graphql';
import { AmsBrowser } from '~/components/ams-picker/ams-browser';
import type { AccountPick } from '~/components/ams-picker/ams-browser-model';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { settle } from '~/components/clients/client-panels';
import { FormError } from '~/components/form-error';
import { MATCH_REASON_LABEL } from '~/lib/ams-match-reason';
import { integrationName } from '~/lib/integrations';

const LINK_FAILED = 'Could not link that AMS account. Please try again.';
const UNLINK_FAILED = 'Could not unlink that AMS account. Please try again.';

/**
 * How one link reads: the AMS it lives in, then the account's own name once a
 * sync has supplied one and its AMS identifier until then. The identifier is
 * not pretty, but the AMS name alone would make "Cara will stop reading Applied
 * Epic" the confirmation for unlinking one account of several.
 */
export const amsAccountLabel = (link: ClientAmsAccountFieldsFragment): string =>
  `${integrationName(link.integrationType)} · ${link.account?.displayName ?? link.amsExternalId}`;

type SuggestedAccount = NonNullable<
  SuggestedAmsAccountsQuery['client']
>['suggestedAmsAccounts'][number];

/**
 * Why a candidate is being offered, in reconciliation's words so one match does
 * not read two ways. The CLIENT_LINK reasons describe an account this client is
 * already linked to, which is what every row on this screen is, so they would be
 * telling the reader where they are.
 */
const suggestionReason = (reason: AmsAccountMatchReason): string | null =>
  reason === AmsAccountMatchReason.ClientLink || reason === AmsAccountMatchReason.ClientLinkNameOnly
    ? null
    : MATCH_REASON_LABEL[reason];

/** The identifiers an agent recognises an account by. */
const suggestionDetail = (account: SuggestedAccount): string =>
  [account.accountCode, account.email, account.phone].filter(Boolean).join(' · ');

/**
 * Candidates ranked against the client, offered before the full AMS search.
 * Empty for an agency with no synced AMS data, which is most of them -- the
 * browser's own live search is the path that always works.
 */
const SuggestedAccounts = (props: {
  suggestions: readonly SuggestedAccount[];
  /** The one being linked, so a click spins its own button rather than all of them. */
  linkingKey: string | null;
  disabled: boolean;
  onLink: (account: SuggestedAccount) => void;
}) => {
  if (props.suggestions.length === 0) {
    return null;
  }
  return (
    <ul className="m-0 mt-2 list-none divide-y divide-neutral-200 p-0 dark:divide-neutral-700">
      {props.suggestions.map((account) => {
        const reason = suggestionReason(account.reason);
        const detail = suggestionDetail(account);
        const key = `${account.integrationId}:${account.externalId}`;
        const name = account.displayName ?? account.externalId;
        return (
          <li key={key} className="flex items-center justify-between gap-4 py-2">
            <div className="min-w-0">
              <p className="m-0 truncate text-sm text-neutral-900 dark:text-neutral-100">{name}</p>
              {detail && (
                <p className="m-0 mt-0.5 truncate text-xs text-neutral-500 dark:text-neutral-400">
                  {detail}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {reason && <Badge color="zinc">{reason}</Badge>}
              <Button
                plain
                className="-my-1.5"
                aria-label={`Link ${name}`}
                loading={props.linkingKey === key}
                disabled={props.disabled}
                onClick={() => props.onLink(account)}
              >
                Link
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

/**
 * The client profile's AMS line: which account this client resolves to, with
 * the actions to change or remove it. Linking goes through the AMS browser, so
 * the search an agent meets here is the one they know from AMS documents.
 */
export const ClientAmsAccountField = (props: {
  clientId: string;
  links: readonly ClientAmsAccountFieldsFragment[];
}) => {
  const [picking, setPicking] = React.useState(false);
  // The link "Change" replaces, if the picker was opened from one. Null when
  // the picker is adding a link rather than replacing one.
  const [changing, setChanging] = React.useState<ClientAmsAccountFieldsFragment | null>(null);
  const [unlinking, setUnlinking] = React.useState<ClientAmsAccountFieldsFragment | null>(null);
  const [linkingSuggestion, setLinkingSuggestion] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const [link, { loading: linking }] = useLinkClientAmsAccountMutation();
  const [unlink, { loading: removing }] = useUnlinkClientAmsAccountMutation();

  // Only worth asking once the client has nothing, and it costs a query per
  // term the client offers.
  const { data: suggested } = useSuggestedAmsAccountsQuery({
    variables: { clientId: props.clientId, limit: 3 },
    skip: props.links.length > 0
  });
  const suggestions = suggested?.client?.suggestedAmsAccounts ?? [];

  const refetch = { refetchQueries: ['GetClientOverview'], awaitRefetchQueries: true };

  // The picker closes before the mutations and the refetch finish, so the row
  // still shows the old account for a moment. Nothing on it is clickable
  // meanwhile, or a second click acts on a link that is already going.
  const busy = linking || removing;

  const linkAccount = async (
    integrationId: string,
    amsExternalId: string,
    knowledgeGraphEntityId?: string | null,
    replacing?: ClientAmsAccountFieldsFragment | null
  ) => {
    setError(null);
    const result = await settle(
      link({
        variables: {
          clientId: props.clientId,
          input: { integrationId, amsExternalId, knowledgeGraphEntityId },
          // The replacement inherits the lead, so changing the account a
          // client resolves to does not quietly hand it to another link.
          isPrimary: replacing?.isPrimary ?? false
        },
        ...refetch
      })
    );
    const message = messageOfFailure(result, LINK_FAILED);
    // Picking the account already linked is how an agent says "keep this one",
    // and the search does not leave a client's own accounts out. Unlinking the
    // pair just relinked would leave the client with nothing.
    const unchanged =
      replacing?.integrationId === integrationId && replacing?.amsExternalId === amsExternalId;
    if (message || !replacing || unchanged) {
      setError(message);
      return;
    }

    // Dropped only once the replacement is stored, so a refused link leaves
    // the client with the account it had.
    const removed = await settle(
      unlink({
        variables: {
          clientId: props.clientId,
          integrationId: replacing.integrationId,
          amsExternalId: replacing.amsExternalId
        },
        ...refetch
      })
    );
    setError(messageOfFailure(removed, UNLINK_FAILED));
  };

  const onPicked = (account: AccountPick) => {
    const replacing = changing;
    setChanging(null);
    // The browser names a record by AMS identity and carries no entity id;
    // the server resolves one from the pair.
    return linkAccount(account.integrationId, account.externalId, null, replacing);
  };

  const onUnlink = async (target: ClientAmsAccountFieldsFragment) => {
    setError(null);
    const result = await settle(
      unlink({
        variables: {
          clientId: props.clientId,
          integrationId: target.integrationId,
          amsExternalId: target.amsExternalId
        },
        ...refetch
      })
    );
    const message = messageOfFailure(result, UNLINK_FAILED);
    setError(message);
    if (!message) {
      setUnlinking(null);
    }
  };

  return (
    <div>
      {props.links.length === 0 ? (
        <div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-neutral-500 dark:text-neutral-400">Not linked to your AMS</span>
            <Button plain className="-my-1.5" loading={linking} onClick={() => setPicking(true)}>
              {suggestions.length > 0 ? 'Search your AMS' : 'Link an account'}
            </Button>
          </div>
          <SuggestedAccounts
            suggestions={suggestions}
            linkingKey={linkingSuggestion}
            disabled={busy}
            onLink={(account) => {
              setLinkingSuggestion(`${account.integrationId}:${account.externalId}`);
              void linkAccount(
                account.integrationId,
                account.externalId,
                account.accountId
              ).finally(() => setLinkingSuggestion(null));
            }}
          />
        </div>
      ) : (
        <ul className="m-0 list-none space-y-1 p-0">
          {props.links.map((item) => (
            <li
              key={`${item.integrationId}:${item.amsExternalId}`}
              className="flex flex-wrap items-center gap-x-2 gap-y-1"
            >
              <span className="truncate">{amsAccountLabel(item)}</span>
              {props.links.length > 1 && item.isPrimary && <Badge color="sky">Primary</Badge>}
              <Button
                plain
                className="-my-1.5"
                disabled={busy}
                onClick={() => {
                  setChanging(item);
                  setPicking(true);
                }}
              >
                Change
              </Button>
              <Button plain className="-my-1.5" disabled={busy} onClick={() => setUnlinking(item)}>
                Unlink
              </Button>
            </li>
          ))}
        </ul>
      )}

      <FormError
        message={unlinking ? null : error}
        className="mt-2 text-xs text-red-600 dark:text-red-400"
      />

      <AmsBrowser
        open={picking}
        onClose={() => {
          setPicking(false);
          setChanging(null);
        }}
        onSelectAccount={(account) => void onPicked(account)}
        // Confirming a set of records is the browser's other mode, which
        // onSelectAccount stops short of.
        onConfirm={() => undefined}
      />

      <ArchiveConfirmDialog
        open={!!unlinking}
        count={1}
        itemNoun="AMS account"
        title="Unlink AMS account"
        confirmLabel="Unlink"
        description={
          <>
            Cara will stop reading <strong>{unlinking ? amsAccountLabel(unlinking) : ''}</strong>{' '}
            for this client. The account itself is untouched, and it can be linked again.
          </>
        }
        loading={removing}
        error={error}
        onCancel={() => {
          setError(null);
          setUnlinking(null);
        }}
        onConfirm={() => void (unlinking && onUnlink(unlinking))}
      />
    </div>
  );
};
